import { useEffect, useMemo, useState } from "react";
import { useStore, type Skill } from "@/lib/store";
import { loadAllTools, type Tool, type ToolSource } from "@/lib/tools";
import { estimateTokenCount } from "@/lib/api";

const SOURCE_LABELS: Record<ToolSource, string> = {
  web: "Web",
  tauri: "Tauri",
  server: "Server",
  mcp: "MCP",
};

type Props = {
  skill: Skill | null;
  mode: "create" | "edit";
  onSaved?: (skill: Skill) => void;
  onDeleted?: () => void;
  onCancel: () => void;
};

export function SkillEditor({ skill, mode, onSaved, onDeleted, onCancel }: Props) {
  const createSkill = useStore((s) => s.createSkill);
  const updateSkill = useStore((s) => s.updateSkill);
  const deleteSkill = useStore((s) => s.deleteSkill);

  const [name, setName] = useState(skill?.name ?? "");
  const [description, setDescription] = useState(skill?.description ?? "");
  const [systemPrompt, setSystemPrompt] = useState(skill?.systemPrompt ?? "");
  const [toolNames, setToolNames] = useState<Set<string>>(new Set(skill?.toolNames ?? []));
  const [autoActivate, setAutoActivate] = useState(skill?.autoActivate ?? true);
  const [tools, setTools] = useState<Tool[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadAllTools().then((all) => {
      if (!cancelled) setTools(all);
    });
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const out: Record<ToolSource, Tool[]> = { web: [], tauri: [], server: [], mcp: [] };
    for (const t of tools) out[t.source].push(t);
    return out;
  }, [tools]);

  const toggle = (toolName: string) => {
    setToolNames((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: trimmed,
        description: description.trim(),
        systemPrompt,
        toolNames: Array.from(toolNames),
        autoActivate,
      };
      const saved = mode === "create"
        ? await createSkill(payload)
        : await updateSkill(skill!.id, payload);
      onSaved?.(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!skill) return;
    setSaving(true);
    try {
      await deleteSkill(skill.id);
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <i className="fa-solid fa-wand-magic-sparkles text-primary" />
        <h2 className="text-sm font-semibold">{mode === "create" ? "New skill" : skill?.name}</h2>
        {error && <span className="ml-auto text-xs text-destructive truncate">{error}</span>}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. coding"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Short description
            <span className="ml-2 text-[10px] opacity-70">shown to the LLM as one sentence</span>
            <span className="ml-2 text-[10px] opacity-70">
              addendum line ~{estimateTokenCount(`- ${name || "(name)"}${description ? ` — ${description}` : ""}`)} tok
            </span>
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Build and edit code artifacts"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            System prompt
            <span className="ml-2 text-[10px] opacity-70">appended when this skill is active</span>
            <span className="ml-2 text-[10px] opacity-70">
              {systemPrompt.length} ch / ~{estimateTokenCount(systemPrompt)} tok
            </span>
          </label>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="When working on code, prefer artifacts over inline code blocks. Use TypeScript by default."
            rows={6}
            className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary font-mono text-[13px]"
          />
        </div>

        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!autoActivate}
            onChange={(e) => setAutoActivate(!e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-muted-foreground">Hide from auto-activation</span>
            <span className="block text-[11px] text-muted-foreground/70">
              The LLM won't see this skill in its list and can't activate it with activate_skill. You can still pin it to a chat.
            </span>
          </span>
        </label>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Tools
            <span className="ml-2 text-[10px] opacity-70">
              {toolNames.size} selected · only available when this skill is active
            </span>
          </label>
          <div className="space-y-3 rounded-md border bg-muted/20 p-3">
            {(["server", "mcp", "tauri", "web"] as ToolSource[]).map((src) => {
              const list = grouped[src];
              if (list.length === 0) return null;
              return (
                <div key={src} className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {SOURCE_LABELS[src]}
                  </p>
                  {list.map((t) => {
                    const checked = toolNames.has(t.name);
                    return (
                      <label key={t.name} className="flex items-start gap-2 text-xs cursor-pointer rounded px-1.5 py-1 hover:bg-muted/40">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(t.name)}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono">{t.name}</div>
                          {t.description && (
                            <p className="text-[11px] text-muted-foreground leading-tight">{t.description}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            })}
            {tools.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">Loading tools…</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t px-4 py-2.5">
        {mode === "edit" && skill && (
          confirmingDelete ? (
            <>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="rounded-md bg-destructive px-2.5 py-1 text-xs text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                Confirm delete
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-destructive"
            >
              <i className="fa-solid fa-trash" /> Delete
            </button>
          )
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border px-3 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
